import * as d3 from "d3";
import { numberText, wrap } from "./helpers";
import { getYAxisRange } from "./timeSeriesHelpers";
import {
  drawTargetSmartLogo,
  getColorScale,
  getTsHeaderLead,
  height,
  width,
} from "./graphHelpers";
import { CALC_TYPE } from "./config";

// // var isIE11 = !!window.MSInputMethodContext && !!document.documentMode;
var margin = { top: 20, right: 20, bottom: 70, left: 50, group: 30 };
var legend_width = 110;
const column_padding = 5;

// // === DRAW DEMOGRAPHIC BAR CHART AND DATA TABLE ====
export function drawBarChart(
  svg,
  calculatedData,
  view_type,
  state,
  comparison_state,
  setToolTip,
  years,
  yearColors,
  yearMapping,
  calc_type,
  vote_mode
) {
  const color_scale = getColorScale(years, yearColors);

  const space = createSvgSpace(svg);
  const y_scale = getYAxisRange(calculatedData);
  const uniqueGroups = getUniqueGroups(calculatedData);
  const uniqueYears = getUniqueYears(calculatedData);

  const { group_width, column_width } = getBarDimensions(
    uniqueGroups,
    uniqueYears
  );
  addGridlines(space, y_scale);

  if (vote_mode) {
    drawStackedBars(
      space,
      calculatedData,
      group_width,
      column_width,
      y_scale,
      setToolTip,
      yearMapping
    );
  } else {
    drawNormalBars(
      space,
      calculatedData,
      group_width,
      column_width,
      y_scale,
      setToolTip,
      yearMapping,
      color_scale,
      years,
      yearColors
    );
  }

  addAxes(
    space,
    calculatedData,
    y_scale,
    group_width,
    uniqueGroups,
    calc_type,
    view_type,
    state,
    comparison_state,
    vote_mode
  );
  addPercBarLabelsOnTop(
    space,
    calculatedData,
    y_scale,
    group_width,
    column_width
  );
  drawTargetSmartLogo(space);
}

function createSvgSpace(svg) {
  return svg
    .append("g")
    .attr("transform", `translate(${margin.left}, ${margin.top})`);
}

function getUniqueGroups(data) {
  const uniqueGroups = [];
  data.forEach((d) => {
    if (!uniqueGroups.includes(d.group)) uniqueGroups.push(d.group);
  });
  return uniqueGroups;
}

function getUniqueYears(data) {
  const uniqueYears = [];
  data.forEach((d) => {
    if (!uniqueYears.includes(d.year)) uniqueYears.push(d.year);
  });
  return uniqueYears;
}

function getUniqueCategories(data) {
  const uniqueCategories = [];
  data.forEach((d) => {
    if (!uniqueCategories.includes(d.key)) uniqueCategories.push(d.key); // Extracts unique demographic keys
  });
  return uniqueCategories;
}

function getBarDimensions(uniqueGroups, uniqueYears) {
  const numCategories = uniqueGroups.length;
  const numYears = uniqueYears.length;
  const group_width =
    (width - margin.right - margin.left - legend_width) / numCategories;
  const column_width =
    (group_width - 2 * column_padding - margin.group) / numYears;

  return { group_width, column_width };
}

function addGridlines(space, y_scale) {
  const gridlines = d3
    .axisLeft()
    .tickFormat("")
    .tickSize(-(width - margin.right - margin.left - legend_width))
    .scale(y_scale);

  space
    .append("g")
    .attr("class", "axis")
    .call(gridlines)
    .call((g) => {
      g.selectAll(".tick")
        .selectAll("line")
        .attr("class", "dashed stroke-gray-500");
      g.selectAll(".domain").remove();
    });
}

function drawNormalBars(
  space,
  data,
  group_width,
  column_width,
  y_scale,
  setToolTip,
  yearMapping,
  color_scale,
  years,
  yearColors
) {
  const bar = space.selectAll(".bar").data(data);

  bar
    .enter()
    .append("rect")
    .attr("class", "bar")
    .attr(
      "x",
      (d) =>
        margin.group / 2 +
        d.group * group_width +
        d.index * (column_padding + column_width)
    )
    .attr("y", (d) => y_scale(d.value))
    .attr("height", (d) => height - margin.bottom - y_scale(d.value))
    .attr("width", column_width)
    .style("fill", (d, i) => color_scale(d.year))
    .on("mouseover", (e, d) => handleMouseOver(e, d, setToolTip, yearMapping))
    .on("mouseout", () => setToolTip({ show: false }))
    .on("mousemove", (e) => handleMouseMove(e, setToolTip));

  drawLegend(space, years, yearColors, yearMapping);
}

function handleStackedMouseOver(e, d, setToolTip, yearMapping, key, label) {
  const rawCountKey = key.replace("_value", "_count");
  const rawCount = d.data[rawCountKey];
  const perc = d3.format(".01%")(rawCount / d.data["vote_count"]);
  setToolTip({
    show: true,
    year: yearMapping[d.data.year],
    voteCount: numberText(rawCount),
    percent: perc,
    demoKey: d.data.key,
    voteMode: label,
  });
  // Add a border to the hovered rectangle
  d3.select(e.target).style("stroke", "black").style("stroke-width", "2px");
}

function handleMouseOver(e, d, setToolTip, yearMapping) {
  setToolTip({
    show: true,
    year: yearMapping[d.year],
    voteCount: numberText(d.vote_count),
    percent: d3.format(".01%")(d.value),
    totalRegistered: numberText(d.total_registered),
    demoKey: d.key,
  });
}

function handleMouseOut(e, setToolTip) {
  setToolTip({ show: false });

  // Remove the border when the mouse is out
  d3.select(e.target).style("stroke", "none");
}

function drawStackedBars(
  space,
  data,
  group_width,
  column_width,
  y_scale,
  setToolTip,
  yearMapping
) {
  const keys = [
    "in_person_early_value",
    "absentee_value",
    "on_eday_value",
    "registered_didnt_vote_value",
    "unregistered_value",
  ];

  const labels = [
    "In-Person Early",
    "Absentee",
    "On Election Day",
    "Registered Didn't Vote",
    "Unregistered",
  ];

  const stack = d3.stack().keys(keys)(data);
  const segmentColorScale = d3
    .scaleOrdinal()
    .domain(keys)
    .range(d3.schemeSpectral[keys.length]);

  space
    .selectAll(".layer")
    .data(stack)
    .enter()
    .append("g")
    .attr("class", "layer")
    .attr("fill", (d) => segmentColorScale(d.key)) // Apply color for each stacked key
    .selectAll("rect")
    .data((d) => d)
    .enter()
    .append("rect")
    .attr(
      "x",
      (d) =>
        margin.group / 2 +
        d.data.group * group_width +
        d.data.index * (column_padding + column_width)
    )
    .attr("y", (d) => y_scale(d[1])) // d[1] is the top of the stack
    .attr("height", (d) => y_scale(d[0]) - y_scale(d[1])) // d[0] is the bottom of the stack
    .attr("width", column_width)
    .on("mouseover", function (e, d) {
      const currentLayerIndex = d3.select(this.parentNode).datum().index;
      const key = keys[currentLayerIndex];
      const label = labels[currentLayerIndex];
      handleStackedMouseOver(e, d, setToolTip, yearMapping, key, label);
    })

    .on("mouseout", (e) => handleMouseOut(e, setToolTip))
    .on("mousemove", (e) => handleMouseMove(e, setToolTip));

  drawStackedLegend(space, labels, segmentColorScale, keys);
  addYearLabels(space, data, group_width, column_width, yearMapping);
}

function handleMouseMove(e, setToolTip) {
  setToolTip({
    top: e.pageY,
    left: e.pageX,
  });
}

function addYearLabels(space, data, group_width, column_width, yearMapping) {
  // Add year labels on the x-axis for vote_mode since we remove the color legend based on year
  space
    .append("g")
    .selectAll(".year-label")
    .data(data)
    .enter()
    .append("text")
    .attr("class", "year-label")
    .attr(
      "x",
      (d) =>
        margin.group / 2 +
        d.group * group_width +
        d.index * (column_padding + column_width) +
        column_width / 2
    ) // Center the label within each group
    .attr("y", height - margin.bottom + 5)
    .attr("dy", ".35em")
    .attr("text-anchor", "middle")
    .attr("font-size", group_width > 70 ? 7 : group_width / 12)
    .text((d) =>
      group_width > 80
        ? yearMapping[d.year]
        : "'" + yearMapping[d.year].substr(2)
    );
}

function addAxes(
  space,
  data,
  y_scale,
  group_width,
  uniqueGroups,
  calc_type,
  view_type,
  state,
  comparison_state,
  vote_mode
) {
  // X axis demographic labels (e.g. Republican, Democrat, etc.)
  const x_scale = d3
    .scaleBand()
    .domain(getUniqueCategories(data))
    .range([0, width - margin.right - margin.left - legend_width]);

  const x_axis = d3
    .axisBottom()
    .scale(x_scale)
    .ticks(uniqueGroups.length + 1);

  space
    .append("g")
    .attr("transform", `translate(0, ${height - margin.bottom})`)
    .attr(
      "class",
      `text-gray-800 ${uniqueGroups.length > 7 ? "font-size-5" : "font-size-6"}`
    )
    .call(x_axis)
    .call((g) => g.select(".domain").remove())
    .selectAll(".tick text")
    .attr("dy", "1.2em")
    .call(wrap, group_width);

  if (vote_mode) {
    removeMiddleTick(space);
  }

  // Y axis percentage labels
  const y_axis = d3
    .axisLeft()
    .scale(y_scale)
    .tickFormat(d3.format(".0%"))
    .ticks(y_scale.domain()[0] / 0.05);

  space
    .append("g")
    .attr("class", "text-gray-800")
    .call(y_axis)
    .call((g) => g.select(".domain").remove());

  addAxisLabels(space, calc_type, view_type, state, comparison_state);
}

function addAxisLabels(space, calc_type, view_type, state, comparison_state) {
  // X and Y axis labels e.g. "% share of ballots cast" and Demographic
  space
    .append("text")
    .attr("fill", "#6c757d")
    .attr("y", height - margin.bottom + margin.group + 4)
    .attr("x", width / 2 - 70)
    .attr("dy", ".5em")
    .attr("class", "font-size-md-10 font-size-lg-6 font-weight-normal")
    .style("text-anchor", "middle")
    .text("Demographic");

  const y_axis_label =
    calc_type === CALC_TYPE.TURNOUT_PERCENT
      ? ": % share of registered voters"
      : ": % share of ballots cast";

  space
    .append("text")
    .attr("transform", "rotate(-90)")
    .attr("y", 0 - margin.left + 3)
    .attr("x", 0 - height / 2 + 15)
    .attr("dy", ".5em")
    .attr("fill", "#6c757d")
    .attr("class", "font-size-md-10 font-size-lg-6 font-weight-normal")
    .style("text-anchor", "middle")
    .text(getTsHeaderLead(view_type, [state, comparison_state]) + y_axis_label);
}

function addPercBarLabelsOnTop(
  space,
  data,
  y_scale,
  group_width,
  column_width
) {
  // Add the percentage labels on the top of each bar
  space
    .append("g")
    .selectAll(".textlabel")
    .data(data)
    .enter()
    .append("text")
    .attr(
      "class",
      `text-center text-anchor-middle text-gray-800 ${data.length > 7 ? "font-size-3" : "x-small"}`
    )
    .attr("fill", "#6c757d")
    .attr(
      "x",
      (d) =>
        margin.group / 2 +
        d.group * group_width +
        d.index * (column_padding + column_width) +
        column_width / 2
    )
    .attr("y", (d) => y_scale(d.value))
    .attr("dy", "-.25em")
    .text((d) => d3.format(".01%")(d.value));
}

function drawLegend(space, years, yearColors, yearMapping) {
  const color_scale = getColorScale(years, yearColors);
  const legend = space
    .selectAll(".legend")
    .data(color_scale.domain().reverse())
    .enter()
    .append("g");

  legend
    .append("rect")
    .attr("class", "legend")
    .attr("width", 20)
    .attr("height", 20)
    .attr("y", (d, i) => {
      const legend_height = color_scale.domain().length * 25;
      const temp = height / 2 - legend_height / 2;
      return temp - i * 25;
    })
    .attr("x", width - legend_width - margin.right)
    .style("fill", (d) => color_scale(d));

  legend
    .append("text")
    .attr("dy", "1em")
    .attr("y", (d, i) => {
      const legend_height = color_scale.domain().length * 25;
      const temp = height / 2 - legend_height / 2;
      return temp - i * 25;
    })
    .attr("x", width - legend_width - margin.right + 25)
    .style("fill", "black")
    .attr("z-index", 1000)
    .text((d) => "- " + yearMapping[d]);
}

function drawStackedLegend(svg, labels, colorScale, keys) {
  // Append the legend to the SVG
  const legend = svg
    .append("g")
    .attr("class", "legend")
    .attr(
      "transform",
      `translate(${width - margin.right - 150}, ${margin.top})`
    ); // Position the legend

  // Create a group for each legend item
  legend
    .selectAll("g")
    .data(labels)
    .enter()
    .append("g")
    .attr("class", "legend-item")
    .attr("transform", (d, i) => `translate(0, ${i * 16})`); // Position each legend item

  // Append rectangles for the legend color swatches
  legend
    .selectAll(".legend-item")
    .append("rect")
    .attr("x", 3)
    .attr("y", 0)
    .attr("width", 15)
    .attr("height", 15)
    .attr("fill", (d, i) => colorScale(keys[i])); // Fill each rect with the corresponding color

  // Append text labels next to the rectangles
  legend
    .selectAll(".legend-item")
    .append("text")
    .attr("x", 25) // Position the text to the right of the rectangle
    .attr("y", 9) // Vertically center the text
    .attr("dy", "0.15em")
    .attr("font-size", 10)
    .text((d) => d); // Use the labels array to show text
}

function removeMiddleTick(space) {
  // Remove the larger middle tick because it blocks the year labels
  let threshold = 2;
  space
    .selectAll(".tick line")
    .filter(function (d, i) {
      return Math.abs(d3.select(this).attr("y2")) > threshold; // Adjust `threshold` accordingly
    })
    .remove();
}
